import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import { get_request_config, LIST_TEMPLATE_VIEW_URL } from '@/utils/system_constants/constant';

export default{
  name: 'TemplateManagement',
  mixins: [alert_mixin],
  data() {
    return {
      search_query: '',
      current_page: 1,
      page_size: 4,
      loading: false,
      templates: [],
      selected_template: null,
      entity_types: [],
      assign_entity_type_id: '',
    }
  },
  mounted(){
    this.get_uploaded_template()
  },
  computed: {
    filtered_templates() {
      const query = (this.search_query || '').toLowerCase().trim();
      if (!query) return this.templates;

      return this.templates.filter((tpl) => {
        const haystack = `${tpl.name} ${tpl.description} ${tpl.type}`.toLowerCase();
        return haystack.includes(query);
      });
    },
    total_pages() {
      const pages = Math.ceil(this.filtered_templates.length / this.page_size);
      return Math.max(1, pages);
    },
    paginated_templates() {
      const start = (this.current_page - 1) * this.page_size;
      const end = start + this.page_size;
      return this.filtered_templates.slice(start, end);
    },
    pagination_label() {
      const total = this.filtered_templates.length;
      if (!total) return `0 of 0 results`;

      const start = (this.current_page - 1) * this.page_size + 1;
      const end = Math.min(this.current_page * this.page_size, total);
      return `${start} to ${end} of ${total} results`;
    },
    metrics() {
      return {
        total_templates: this.templates.length,
        active_assignments: this.templates.reduce((count, template) => {
          const template_assignments = Array.isArray(template?.raw?.template_entity_types)
            ? template.raw.template_entity_types
            : Array.isArray(template?.template_entity_types)
              ? template.template_entity_types
              : [];

          const active_assignments = template_assignments.filter((assignment) => assignment?.is_assigned !== false);
          return count + active_assignments.length;
        }, 0),
      };
    }
  },
  watch: {
    search_query() {
      this.current_page = 1;
    }
  },
  methods: {
    extract_assignment_identifier(assignment = {}) {
      return String(
        assignment?.template_document_entity_type_id ||
        assignment?.template_entity_type_id ||
        assignment?.template_document_entity_type ||
        assignment?.id ||
        '',
      ).trim();
    },
    extract_assignment_workflow_identifier(assignment = {}) {
      return String(
        assignment?.workflow_id ||
        assignment?.assigned_workflow_id ||
        assignment?.workflow ||
        '',
      ).trim();
    },
    parse_response_data(payload) {
      if (typeof payload !== 'string') {
        return payload;
      }

      const trimmed = payload.trim();
      if (!trimmed) {
        return {};
      }

      try {
        return JSON.parse(trimmed);
      } catch (error) {
        return {
          status: 'error',
          message: 'Invalid JSON response received from the server.'
        };
      }
    },
    go_to_upload_template() {
      this.$router.push({ name: 'UploadTemplate' });
    },
    get_active_template_bindings(template) {
      const template_assignments = Array.isArray(template?.raw?.template_entity_types)
        ? template.raw.template_entity_types
        : Array.isArray(template?.template_entity_types)
          ? template.template_entity_types
          : [];

      return template_assignments.filter((assignment) => assignment?.is_assigned !== false);
    },
    has_active_template_binding(template) {
      return Boolean(this.get_active_template_bindings(template).length);
    },
    go_to_template_setup(template) {
      const template_id = Number(template?.id || 0);
      const active_binding = this.get_active_template_bindings(template)[0] || null;
      const template_entity_type_id = this.extract_assignment_identifier(active_binding);
      const workflow_id = this.extract_assignment_workflow_identifier(active_binding);

      if (!template_id) {
        this.show_error('Template context missing. Please select a valid template.');
        return;
      }

      if (!active_binding || !template_entity_type_id) {
        this.show_warning(
          'Template Binding Required',
          'This template is not bound yet. Please bind it to an entity type before opening template setup.',
        );
        return;
      }

      this.$router.push({
        name: 'EsigningSetup',
        query: {
          template_id: String(template_id),
          template_name: template?.name ? String(template.name) : '',
          template_type: template?.type ? String(template.type) : '',
          template_entity_type_id,
          ...(workflow_id ? { workflow_id } : {}),
          setup_tab: 'forms',
        },
      });
    },
    set_page(page) {
      const next = Math.min(Math.max(1, page), this.total_pages);
      this.current_page = next;
    },
    preview_template(template) {
      this.selected_template = {
        ...template,
        preview_url: template.presigned_url
      };
    },
    assign_template(template) {
      this.selected_template = template;
      this.assign_entity_type_id = '';
    },
    confirm_assign() {
      this.selected_template = null;
      this.assign_entity_type_id = '';
    },
    async get_uploaded_template() {
      this.loading = true;

      try {
        // Fetch only e-signing templates for this view
        const esign_response = await axios.get(LIST_TEMPLATE_VIEW_URL, get_request_config());
        const esign_response_data = this.parse_response_data(esign_response.data);

        if (esign_response_data.status === "success") {
          const formatDate = (value) => {
            if (!value) return '';
            const dt = new Date(value);
            if (Number.isNaN(dt.getTime())) return '';
            return new Intl.DateTimeFormat('en-US', {
              month: 'short',
              day: '2-digit',
              year: 'numeric'
            }).format(dt);
          };
          this.templates = (esign_response_data.data || []).map((template) => ({
            id: template.id,
            name: template.name || template.template_name,
            description: template.description || template.template_description,
            type: template.template_type || template.type || "e-signing",
            date_uploaded: template.date_uploaded || formatDate(template.created_at || template.updated_at),
            url: template.url || template.document_url || template.presigned_url,
            presigned_url: template.presigned_url,
            raw: template,
          }));
        } else {
          this.show_error(esign_response_data.message || "Failed to load e-signing templates");
        }
      } catch (error) {
        this.show_error(
          `Error fetching templates: ${error.response?.data?.message || error.message
          }`
        );
      } finally {
        this.loading = false;
      }
    },

  },
}
